import React, { createContext, useContext, useReducer, ReactNode } from 'react'

export interface UploadedDocument {
  id: string
  name: string
  content: string
  type: string
  size: number
  uploadedAt: Date
  isImage?: boolean
  imageUrl?: string
}

export interface DocumentState {
  content: string
  title: string
  fontSize: number
  fontFamily: string
  isBold: boolean
  isItalic: boolean
  isUnderline: boolean
  alignment: 'left' | 'center' | 'right' | 'justify'
  lineHeight: number
  wordCount: number
  characterCount: number
  lastSaved: Date | null
  isModified: boolean
  uploadedDocuments: UploadedDocument[]
}

type DocumentAction =
  | { type: 'SET_CONTENT'; payload: string }
  | { type: 'SET_TITLE'; payload: string }
  | { type: 'SET_FONT_SIZE'; payload: number }
  | { type: 'SET_FONT_FAMILY'; payload: string }
  | { type: 'TOGGLE_BOLD' }
  | { type: 'TOGGLE_ITALIC' }
  | { type: 'TOGGLE_UNDERLINE' }
  | { type: 'SET_ALIGNMENT'; payload: 'left' | 'center' | 'right' | 'justify' }
  | { type: 'SET_LINE_HEIGHT'; payload: number }
  | { type: 'UPDATE_COUNTS'; payload: { wordCount: number; characterCount: number } }
  | { type: 'SET_SAVED' }
  | { type: 'SET_MODIFIED'; payload: boolean }
  | { type: 'ADD_UPLOADED_DOCUMENT'; payload: UploadedDocument }
  | { type: 'REMOVE_UPLOADED_DOCUMENT'; payload: string }

const initialState: DocumentState = {
  content: '',
  title: 'Untitled Document',
  fontSize: 12,
  fontFamily: 'Times New Roman',
  isBold: false,
  isItalic: false,
  isUnderline: false,
  alignment: 'left',
  lineHeight: 1.5,
  wordCount: 0,
  characterCount: 0,
  lastSaved: null,
  isModified: false,
  uploadedDocuments: [],
}

function documentReducer(state: DocumentState, action: DocumentAction): DocumentState {
  switch (action.type) {
    case 'SET_CONTENT':
      return { ...state, content: action.payload, isModified: true }
    case 'SET_TITLE':
      return { ...state, title: action.payload, isModified: true }
    case 'SET_FONT_SIZE':
      return { ...state, fontSize: action.payload }
    case 'SET_FONT_FAMILY':
      return { ...state, fontFamily: action.payload }
    case 'TOGGLE_BOLD':
      return { ...state, isBold: !state.isBold }
    case 'TOGGLE_ITALIC':
      return { ...state, isItalic: !state.isItalic }
    case 'TOGGLE_UNDERLINE':
      return { ...state, isUnderline: !state.isUnderline }
    case 'SET_ALIGNMENT':
      return { ...state, alignment: action.payload }
    case 'SET_LINE_HEIGHT':
      return { ...state, lineHeight: action.payload }
    case 'UPDATE_COUNTS':
      return { ...state, wordCount: action.payload.wordCount, characterCount: action.payload.characterCount }
    case 'SET_SAVED':
      return { ...state, lastSaved: new Date(), isModified: false }
    case 'SET_MODIFIED':
      return { ...state, isModified: action.payload }
    case 'ADD_UPLOADED_DOCUMENT':
      return { ...state, uploadedDocuments: [...state.uploadedDocuments, action.payload] }
    case 'REMOVE_UPLOADED_DOCUMENT':
      return { ...state, uploadedDocuments: state.uploadedDocuments.filter(doc => doc.id !== action.payload) }
    default:
      return state
  }
}

interface DocumentContextType {
  state: DocumentState
  dispatch: React.Dispatch<DocumentAction>
}

const DocumentContext = createContext<DocumentContextType | undefined>(undefined)

export const useDocument = () => {
  const context = useContext(DocumentContext)
  if (context === undefined) {
    throw new Error('useDocument must be used within a DocumentProvider')
  }
  return context
}

interface DocumentProviderProps {
  children: ReactNode
}

export const DocumentProvider: React.FC<DocumentProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(documentReducer, initialState)

  return (
    <DocumentContext.Provider value={{ state, dispatch }}>
      {children}
    </DocumentContext.Provider>
  )
} 